'use client';

import React, { useState } from 'react';
import { AlertTriangle, XCircle, Clock, Bell, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { Credential } from '../page';

interface RenewalAlertsProps {
  credentials: Credential[];
}

function ExpiryBadge({ days }: { days: number }) {
  if (days < 0) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-danger/10 border border-danger/20">
        <XCircle size={13} className="text-danger" />
        <span className="text-xs font-700 text-danger">{Math.abs(days)}d overdue</span>
      </div>
    );
  }
  if (days <= 7) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-danger/10 border border-danger/20">
        <Clock size={13} className="text-danger" />
        <span className="text-xs font-700 text-danger">{days}d left</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-warning/10 border border-warning/20">
      <AlertTriangle size={13} className="text-warning" />
      <span className="text-xs font-700 text-warning">{days}d left</span>
    </div>
  );
}

function UrgencyBar({ days }: { days: number }) {
  const pct = days < 0 ? 100 : Math.max(0, Math.min(100, ((30 - days) / 30) * 100));
  const color = days < 0 ? 'bg-danger' : days <= 7 ? 'bg-danger' : 'bg-warning';
  return (
    <div className="h-1 bg-muted rounded-full overflow-hidden mt-2">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function RenewalAlerts({ credentials }: RenewalAlertsProps) {
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());
  const [expandedStaff, setExpandedStaff] = useState<Set<string>>(new Set());

  const expired = credentials.filter((c) => c.status === 'expired');
  const expiringSoon = credentials.filter((c) => c.status === 'expiring_soon');

  // Group by staff
  const byStaff = new Map<string, { name: string; role: string; dept: string; items: Credential[] }>();
  credentials.forEach((c) => {
    if (!byStaff.has(c.staffId)) {
      byStaff.set(c.staffId, { name: c.staffName, role: c.role, dept: c.department, items: [] });
    }
    byStaff.get(c.staffId)!.items.push(c);
  });

  function toggleAck(id: string) {
    setAcknowledged((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleStaff(id: string) {
    setExpandedStaff((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      {/* Summary banner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-danger/10 border border-danger/20 rounded-xl p-4 flex items-center gap-3">
          <XCircle size={24} className="text-danger flex-shrink-0" />
          <div>
            <p className="text-2xl font-700 text-danger">{expired.length}</p>
            <p className="text-xs text-danger/80 font-500">Expired credentials</p>
          </div>
        </div>
        <div className="bg-warning/10 border border-warning/20 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={24} className="text-warning flex-shrink-0" />
          <div>
            <p className="text-2xl font-700 text-warning">{expiringSoon.length}</p>
            <p className="text-xs text-warning/80 font-500">Expiring within 30 days</p>
          </div>
        </div>
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-center gap-3">
          <Bell size={24} className="text-primary flex-shrink-0" />
          <div>
            <p className="text-2xl font-700 text-primary">{byStaff.size}</p>
            <p className="text-xs text-primary/80 font-500">Staff requiring action</p>
          </div>
        </div>
      </div>

      {/* Alert cards grouped by staff */}
      <div className="space-y-3">
        <h3 className="text-sm font-600 text-foreground">Action Required by Staff</h3>
        {Array.from(byStaff.entries()).map(([staffId, staff]) => {
          const isExpanded = expandedStaff.has(staffId);
          const hasExpired = staff.items.some((c) => c.status === 'expired');
          const urgentCount = staff.items.filter((c) => c.daysUntilExpiry <= 7).length;
          return (
            <div key={staffId} className={`bg-card border rounded-xl overflow-hidden ${hasExpired ? 'border-danger/30' : 'border-warning/30'}`}>
              {/* Staff header */}
              <button
                onClick={() => toggleStaff(staffId)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-full bg-primary/10 text-primary text-sm font-700 flex items-center justify-center flex-shrink-0">
                  {staff.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-600 text-foreground text-sm">{staff.name}</p>
                  <p className="text-xs text-muted-foreground">{staff.role} · {staff.dept}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {hasExpired && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-700 bg-danger/10 text-danger">
                      {staff.items.filter((c) => c.status === 'expired').length} expired
                    </span>
                  )}
                  {urgentCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-700 bg-danger/10 text-danger">
                      {urgentCount} urgent
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-600 bg-muted text-muted-foreground">
                    {staff.items.length} credential{staff.items.length !== 1 ? 's' : ''}
                  </span>
                  {isExpanded ? <ChevronUp size={15} className="text-muted-foreground" /> : <ChevronDown size={15} className="text-muted-foreground" />}
                </div>
              </button>

              {/* Credential rows */}
              {isExpanded && (
                <div className="border-t border-border divide-y divide-border">
                  {staff.items.map((c) => {
                    const isAck = acknowledged.has(c.id);
                    return (
                      <div key={c.id} className={`px-4 py-3 flex items-start gap-3 ${isAck ? 'opacity-50' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-500 text-foreground">{c.credentialName}</p>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">{c.category}</span>
                            {c.renewalSubmitted && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-600">Renewal Submitted</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{c.issuingBody} · #{c.licenseNumber}</p>
                          <p className="text-xs text-muted-foreground">Expires: {c.expiryDate}</p>
                          {c.notes && <p className="text-xs text-muted-foreground italic mt-0.5">{c.notes}</p>}
                          <UrgencyBar days={c.daysUntilExpiry} />
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                          <ExpiryBadge days={c.daysUntilExpiry} />
                          <button
                            onClick={() => toggleAck(c.id)}
                            title={isAck ? 'Unacknowledge' : 'Acknowledge'}
                            className={`p-1.5 rounded-md transition-colors ${isAck ? 'bg-success/10 text-success' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`}
                          >
                            <CheckCircle size={15} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {credentials.length === 0 && (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <CheckCircle size={40} className="text-success mx-auto mb-3" />
            <p className="text-foreground font-600">All credentials are up to date</p>
            <p className="text-sm text-muted-foreground mt-1">No renewals required at this time.</p>
          </div>
        )}
      </div>

      {/* Upcoming timeline */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-600 text-foreground mb-4">Renewal Timeline</h3>
        <div className="space-y-2">
          {credentials.slice(0, 8).map((c) => (
            <div key={c.id} className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${c.daysUntilExpiry < 0 ? 'bg-danger' : c.daysUntilExpiry <= 7 ? 'bg-danger' : 'bg-warning'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-500 text-foreground truncate">{c.staffName} — {c.credentialName}</p>
                  <span className="text-xs text-muted-foreground flex-shrink-0">{c.expiryDate}</span>
                </div>
                <div className="h-1 bg-muted rounded-full overflow-hidden mt-1">
                  <div
                    className={`h-full rounded-full ${c.daysUntilExpiry < 0 ? 'bg-danger' : c.daysUntilExpiry <= 7 ? 'bg-danger' : 'bg-warning'}`}
                    style={{ width: `${c.daysUntilExpiry < 0 ? 100 : Math.min(100, ((30 - c.daysUntilExpiry) / 30) * 100)}%` }}
                  />
                </div>
              </div>
              <ExpiryBadge days={c.daysUntilExpiry} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
